import { Component, AfterViewInit, OnInit, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Matter } from '../../../models/matter';

import { MatterService } from '../../../services/matters/matter.service';
import { NotyService } from '../../../services/common/noty.service';

@Component({
  selector: 'app-matter-index',
  templateUrl: './matter-index.component.html',
  styleUrls: ['./matter-index.component.scss'],
})
export class MatterIndexComponent implements OnInit {
  /**
   * Bulk edit options
   * @type {any}
   */
  bulkEditOptions: any = {
    default_appointment_addresses: [],
    detail_types: [],
    matter_user_types: [],
    matter_users: [],
  };

  /**
   * Indicates if bulk edit options are loaded
   * @type {boolean}
   */
  bulkEditOptionsLoaded: boolean = false;

  /**
   * Bulk edit request params
   * @type {any}
   */
  bulkEditParams: any = {};

  /**
   * Count of total matters of search result
   * @type {number}
   */
  count: number = 0;

  /**
   * Matter search results for current page
   * @type {Array<Matter>}
   */
  matters: Array<Matter> = [];

  /**
   * Search options for sidebar
   * @type {any}
   */
  searchOptions: any = {
    active_matter_users: [],
    all_matter_users: [],
    default_appointment_addresses: [],
    matter_statuses: [],
    matter_user_types: [],
    order_by: [],
    order_direction: [],
    per_page: 20,
    rate_types: [],
    referral_types: [],
    role_types: []
  };

  /**
   * Search params for search request
   * @type {any}
   */
  searchParams: any = {
    page: 1,
    per_page: 20,
  };

  /**
   * Indicates if search request is being proccessed
   * @type {boolean}
   */
  searching: boolean = false;

  /**
   * Indicates if bulk edit form is to be shown
   * @type {boolean}
   */
  showBulkEdit: boolean = false;

  /**
   * Indicates if sidebar is to be maximized
   * @type {boolean}
   */
  sidebarMaximized: boolean = false;

  /**
   * Indicates if sidebar is to be shown for smaller width screens
   * @type {boolean}
   */
  sidebarOpen: boolean = false;

  /**
   * Indicates if bulk edit request is being  processed
   * @type {boolean}
   */
  submittingBulkEdit: boolean = false;

  selectedMatterUserType: any;
  selectedRoleType: any;

  /**
   * Constructor
   * @param {ActivatedRoute} private activatedRoute Angular current route with snapshot
   * @param {MatterService}  private matterService  Service to access matters controller
   * @param {NotyService}    private notyService    Notifications service for web UI
   * @param {Router}         private router         Angular router to navigate URLs
   */
  constructor(private activatedRoute: ActivatedRoute, private matterService: MatterService, private notyService: NotyService, private router: Router) {}

  // Component lifecycle methods

  /**
   * Called on component initialization
   */
  ngOnInit() {
    this.matterService.searchOptions().then((response_json) => {
      this.searchOptions = response_json;
      this.setSearchParams();
      this.search();
    });
  }

  /**
   * Called after HTML is initialized
   */
  ngAfterViewInit() {
    setTimeout(() => {
      this.content_sidebar_scroll();
      this.search_height_maintainer();
    }, 500);
    setTimeout(() => {
      this.content_sidebar_scroll();
      this.search_height_maintainer();
    }, 5000);
  }

  // Component methods

  /**
   * Clear search fields
   */
  clearSearch() {
    this.searchParams = {
      page: 1,
      per_page: this.searchOptions.per_page || 20,
    };
    this.search();
  }

  /**
   * Open matter
   * @param {number} matterId Id of matter
   */
  openMatter(matterId: number) {
    this.router.navigateByUrl(`/matters/${matterId}`);
  }

  /**
   * Page changed
   * @param {any} $event Page change event with new page number
   */
  pageChanged($event: any) {
    this.searchParams.page = $event.page;
    this.search();
  }

  /**
   * Process search request
   * @param {any = null} pageEvent Page change event with page change details, present if a pagination button is clicked
   */
  search(pageEvent: any = null) {
    if (pageEvent) this.searchParams.page = pageEvent.page;
    if (this.searchParams.date_from_struct) this.searchParams.date_from = `${this.searchParams.date_from_struct.month}/${this.searchParams.date_from_struct.day}/${this.searchParams.date_from_struct.year}`;
    else this.searchParams.date_from = undefined;
    if (this.searchParams.date_to_struct) this.searchParams.date_to = `${this.searchParams.date_to_struct.month}/${this.searchParams.date_to_struct.day}/${this.searchParams.date_to_struct.year}`;
    else this.searchParams.date_to = undefined;

    this.searching = true;
    this.matterService.index(this.searchParams).then((response_json) => {
      this.matters = response_json.matters || [];
      this.count = response_json.count || 0;
      this.searching = false;
    }).catch((error) => {
      this.searching = false;
      this.notyService.notify('Error loading matters', 'error');
    });
  }

  /**
   * Set search params from query params
   */
  setSearchParams() {
    // Copy queryParams to avoid mutating the immutable queryParams object
    const queryParams = this.activatedRoute.snapshot.queryParams;
    this.searchParams = {
      page: queryParams.page || 1,
      per_page: queryParams.per_page || this.searchOptions.per_page || 20,
      ...queryParams
    };
  }

  /**
   * Set query params if search is done
   */
  setQueryParams() {
    this.router.navigateByUrl('/matters', /* Removed unsupported properties by Angular migration: queryParams. */ {});
  }

  // Positioning events and methods
  @HostListener('window:scroll', ['$event'])
  window_scroll(event: Event) {
    this.content_sidebar_scroll();
    this.search_height_maintainer();
  }

  @HostListener('window:resize', ['$event'])
  window_resize(event: Event) {
    this.content_sidebar_scroll();
    this.search_height_maintainer();
  }

  content_sidebar_scroll() {
    var content_main = document.getElementById('content-main');
    var content_sidebar = document.getElementById('content-sidebar');
    var sidebar_height = window.innerHeight;
    if (content_sidebar && content_main) {
      if (content_main.getBoundingClientRect().top > 19) {
        content_sidebar.style.position = 'absolute';
        content_sidebar.style.top = content_main.offsetTop + 'px';
        sidebar_height -= content_main.getBoundingClientRect().top + 20;
        //console.log('greater ' + matter_sidebar.style.top);
      } else {
        content_sidebar.style.position = 'fixed';
        content_sidebar.style.top = '20px';
        sidebar_height -= content_sidebar.getBoundingClientRect().top + 20;
        //console.log('lesser ' + matter_sidebar.style.top);
      }

      if (content_sidebar.clientHeight != sidebar_height) content_sidebar.style.height = sidebar_height + 'px';
    }
  }

  search_height_maintainer() {
    var content_sidebar = document.getElementById('content-sidebar');
    var content_search = document.getElementById('search-form');
    if (content_sidebar && content_search) {
      var search_height = content_sidebar.clientHeight - 95;
    }
  }

  bulkEdit() {

  }

  deselectAll() {

  }

  addMatterUserTypeToSearch(matterUserType: any) {

  }

  removeMatterUserTypeFromSearch(matterUserType: any) {
    
  }

  addRoleTypeToSearch(roleType: any) {
    
  }

  removeRoleTypeFromSearch(roleType: any) {
    
  }
}
